<template>
  <CognitoBrandedShell>
    <div
      class="w-full max-w-md space-y-6 rounded-2xl border border-gray-200 bg-white p-8 text-left shadow-lg sm:text-center"
    >
      <div class="space-y-3">
        <p
          v-if="errorHeading"
          class="text-lg font-semibold leading-snug text-red-600"
        >
          {{ errorHeading }}
        </p>
        <p class="whitespace-pre-line text-base leading-relaxed text-gray-600">
          {{ errorDetail }}
        </p>
      </div>
      <p class="text-sm text-gray-500">
        <template v-if="nextStepIsFreshSignIn">
          Signing you out so you can sign up again in
          {{ redirectSecondsRemaining }} seconds…
        </template>
        <template v-else-if="nextStepIsCompanyHome">
          Redirecting to the expert app in
          {{ redirectSecondsRemaining }}
          seconds... {{ redirectSecondsRemaining === 1 ? "" : "s" }}.
        </template>
        <template v-else>
          Redirecting to the expert app in
          {{ redirectSecondsRemaining }} seconds…
        </template>
      </p>
      <button
        type="button"
        class="w-full rounded-lg bg-[#9489F5] px-4 py-3 text-sm font-medium text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#9489F5] focus:ring-offset-2 focus:ring-offset-white"
        @click="completeInterstitial"
      >
        {{ primaryButtonLabel }}
      </button>
    </div>
  </CognitoBrandedShell>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import CognitoBrandedShell from "@/components/cognito/CognitoBrandedShell.vue";
import {
  clearCognitoSessionLocalOnly,
  cognitoSignInFresh,
} from "@/cognito/auth";
import {
  companyInvite422HandlingFromCode,
  companyInviteErrorDisplayForCode,
} from "@/cognito/cognitoCallbackErrors";
import { buildExpertAppUrlWithCompanyInviteError } from "@/cognito/expertAppInviteErrorRedirect";

const DEFAULT_ERROR_CODE = "company_email_invite_invalid";

export default defineComponent({
  name: "CompanyInviteExpertRedirectView",
  components: { CognitoBrandedShell },

  setup() {
    const route = useRoute();
    const errorHeading = ref("");
    const errorDetail = ref("");
    const nextStepIsFreshSignIn = ref(false);
    const nextStepIsCompanyHome = ref(false);
    const primaryButtonLabel = ref("Continue to expert app");
    const redirectSecondsRemaining = ref(10);
    let redirectIntervalId: ReturnType<typeof setInterval> | null = null;
    let isNavigating = false;

    function clearRedirectCountdown(): void {
      if (redirectIntervalId !== null) {
        clearInterval(redirectIntervalId);
        redirectIntervalId = null;
      }
    }

    function resolveCode(): string {
      const raw = route.query.company_invite_error;
      const s = Array.isArray(raw) ? raw[0] : raw;
      const t = typeof s === "string" ? s.trim() : "";
      return t || DEFAULT_ERROR_CODE;
    }

    function completeInterstitial(): void {
      if (isNavigating) return;
      isNavigating = true;
      clearRedirectCountdown();
      const code = resolveCode();
      const handling = companyInvite422HandlingFromCode(code);
      if (handling.strategy === "cognito_sign_in_fresh") {
        void cognitoSignInFresh();
        return;
      }
      if (handling.strategy === "company_app_root_sign_in") {
        void (async () => {
          await clearCognitoSessionLocalOnly();
          await cognitoSignInFresh();
        })();
        return;
      }
      window.location.assign(
        buildExpertAppUrlWithCompanyInviteError(handling.code)
      );
    }

    function startRedirectCountdown(): void {
      clearRedirectCountdown();
      redirectSecondsRemaining.value = 10;
      redirectIntervalId = window.setInterval(() => {
        if (redirectSecondsRemaining.value <= 1) {
          clearRedirectCountdown();
          completeInterstitial();
          return;
        }
        redirectSecondsRemaining.value -= 1;
      }, 1000);
    }

    onMounted(() => {
      const code = resolveCode();
      const handling = companyInvite422HandlingFromCode(code);
      nextStepIsFreshSignIn.value =
        handling.strategy === "cognito_sign_in_fresh";
      nextStepIsCompanyHome.value =
        handling.strategy === "company_app_root_sign_in";
      if (nextStepIsFreshSignIn.value) {
        primaryButtonLabel.value = "Continue to sign in";
      } else if (nextStepIsCompanyHome.value) {
        primaryButtonLabel.value = "Return to home";
      } else {
        primaryButtonLabel.value = "Continue to expert app";
      }
      const display = companyInviteErrorDisplayForCode(code);
      errorHeading.value = display.heading;
      errorDetail.value = display.detail;
      startRedirectCountdown();
    });

    onUnmounted(() => {
      clearRedirectCountdown();
    });

    return {
      errorHeading,
      errorDetail,
      nextStepIsFreshSignIn,
      nextStepIsCompanyHome,
      primaryButtonLabel,
      redirectSecondsRemaining,
      completeInterstitial,
    };
  },
});
</script>
